import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Button,
  Input,
  FormLabel,
  Snackbar,
  Alert,
  IconButton,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  ImageSearch as ImageSearchIcon
} from "@mui/icons-material";

interface BannerImage {
  id: number;
  type: string;
  filename: string;
  base64: string;
  title?: string;
  subtitle?: string;
}

const BASE_URL = "https://api.growl.games/api/banners";

const Banner: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [banners, setBanners] = useState<BannerImage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedBanner, setSelectedBanner] = useState<{
    type: string;
    file: File | null;
    base64Preview?: string;
    title: string;
    subtitle: string;
  } | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState<string>('');
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/getAllImagesBinary`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.data.status === 'success') {
        setBanners(response.data.images);
      } else {
        handleSnackbarOpen('Failed to fetch banners', 'error');
      }
    } catch {
      handleSnackbarOpen('Failed to fetch banners', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSnackbarOpen = (message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });

  const handleBannerSelect = (banner: BannerImage) => {
    setIsCreating(false);
    setSelectedBanner({
      type: banner.type,
      file: null,
      base64Preview: banner.base64,
      title: banner.title || '',
      subtitle: banner.subtitle || '',
    });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.length) {
      const file = event.target.files[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedBanner(prev => ({
          ...prev!,
          file,
          base64Preview: reader.result as string,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Create new banner
  const createBanner = async () => {
    if (!selectedBanner || !selectedBanner.file) {
      handleSnackbarOpen('Please select an image and type', 'error');
      return;
    }

    const formData = new FormData();
    formData.append('type', selectedBanner.type);
    formData.append('image', selectedBanner.file);
    formData.append('title', selectedBanner.title || '');
    formData.append('subtitle', selectedBanner.subtitle || '');

    try {
      setIsLoading(true);
      await axios.post(`${BASE_URL}/createBanner`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      handleSnackbarOpen('Banner created successfully');
      await fetchBanners();
      setOpenCreateDialog(false);
      setSelectedBanner(null);
      setIsCreating(false);
    } catch {
      handleSnackbarOpen('Failed to create banner', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  //Update existing banner
  const updateBanner = async () => {
    if (!selectedBanner) {
      handleSnackbarOpen('Please select a banner', 'error');
      return;
    }

    const formData = new FormData();
    formData.append('type', selectedBanner.type);
    if (selectedBanner.file) {
      formData.append('image', selectedBanner.file);
    }
    formData.append('title', selectedBanner.title || '');
    formData.append('subtitle', selectedBanner.subtitle || '');

    try {
      setIsLoading(true);
      await axios.post(`${BASE_URL}/updateBanner`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      handleSnackbarOpen('Banner updated successfully');
      await fetchBanners();
      setSelectedBanner(null);
    } catch {
      handleSnackbarOpen('Failed to update banner', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const openPreviewDialog = (base64: string) => {
    setPreviewImage(base64);
    setPreviewDialogOpen(true);
  };

  const renderBannerSkeletons = () => (
    Array.from(new Array(6)).map((_, index) => (
      <Grid item xs={12} sm={6} md={4} key={index}>
        <Box>
          <Skeleton variant="rectangular" width="100%" height={200} sx={{ borderRadius: 2 }} />
          <Skeleton width="60%" sx={{ mx: 'auto', mt: 1 }} />
        </Box>
      </Grid>
    ))
  );


  const [openCreateDialog, setOpenCreateDialog] = useState(false);

  return (
    <div className="ml-0 lg:ml-[256px] p-2">
      <Box sx={{ width: '100%', maxWidth: 1000, margin: 'auto', padding: 2 }}>
        <Card elevation={6} sx={{ borderRadius: 3 }}>
          <CardHeader
            title="Banner Management"
            titleTypographyProps={{ variant: 'h4', color: 'primary', align: 'center' }}
          />
          <CardContent>
            <Box sx={{ mb: 2, textAlign: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setIsCreating(true);
                  setSelectedBanner({ type: '', file: null, title: '', subtitle: '' });
                  setOpenCreateDialog(true);
                }}
              >
                + Add New Banner
              </Button>

            </Box>

            <Grid container spacing={3}>
              {isLoading
                ? renderBannerSkeletons()
                : banners.map(banner => (
                  <Grid item xs={12} sm={6} md={4} key={banner.id}>
                    <Box
                      sx={{
                        position: 'relative',
                        border: '2px solid',
                        borderColor:
                          selectedBanner?.type === banner.type ? 'primary.main' : 'grey.300',
                        borderRadius: 2,
                        padding: 1,
                        cursor: 'pointer',
                        backgroundColor:
                          selectedBanner?.type === banner.type ? 'primary.light' : 'transparent',
                      }}
                      onClick={() => handleBannerSelect(banner)}
                    >
                      <img
                        src={`data:image/png;base64,${banner.base64}`}
                        alt={banner.type}
                        style={{ width: '100%', height: 200, objectFit: 'cover', borderRadius: 8 }}
                      />
                      <Box sx={{ textAlign: 'center', mt: 1 }}>
                        {banner.title && (
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            {banner.title}
                          </Typography>
                        )}
                        {banner.subtitle && (
                          <Typography variant="body2">{banner.subtitle}</Typography>
                        )}
                      </Box>

                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          openPreviewDialog(banner.base64);
                        }}
                        sx={{
                          position: 'absolute',
                          top: 10,
                          right: 10,
                          zIndex: 10,
                          backgroundColor: 'rgba(255,255,255,0.7)',
                          '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' }
                        }}
                      >
                        <ImageSearchIcon />
                      </IconButton>
                    </Box>
                  </Grid>
                ))}
            </Grid>

            {/* Form for Create / Update */}
            {!isLoading && selectedBanner && (
              <Box sx={{ mt: 4, textAlign: 'center' }}>
                {/* <FormLabel sx={{ display: 'block', mb: 2, fontWeight: 'bold' }}>
                  {isCreating ? "Create New Banner" : `Selected Banner Type: ${selectedBanner.type}`}
                </FormLabel> */}

                {/* <Input
                  type="text"
                  placeholder="Enter banner type"
                  value={selectedBanner.type}
                  onChange={(e) =>
                    setSelectedBanner(prev => ({ ...prev!, type: e.target.value }))
                  }
                  fullWidth
                  sx={{ mb: 2 }}
                  disabled={!isCreating}
                /> */}

                <Input
                  type="file"
                  inputProps={{ accept: '.png,.jpg,.jpeg', style: { display: 'none' } }}
                  onChange={handleFileChange}
                  id="banner-file-input"
                />
                <label htmlFor="banner-file-input">
                  <Button
                    component="span"
                    variant="contained"
                    color="primary"
                    startIcon={<CloudUploadIcon />}
                    sx={{ mb: 2 }}
                  >
                    Choose File
                  </Button>
                </label>

                {selectedBanner.base64Preview && (
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                    <img
                      src={selectedBanner.base64Preview}
                      alt="Preview"
                      style={{
                        maxWidth: '100%',
                        maxHeight: 300,
                        objectFit: 'contain',
                        borderRadius: 8,
                        boxShadow: theme.shadows[3],
                        padding: 4,
                      }}
                    />
                  </Box>
                )}

                {/* {selectedBanner.base64Preview ? (
                  <Box
                    sx={{
                      mb: 2,
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <img
                      src={selectedBanner.base64Preview}
                      alt="Preview"
                      style={{
                        maxWidth: '100%',
                        maxHeight: 300,
                        objectFit: 'contain',
                        borderRadius: 8,
                        boxShadow: theme.shadows[3],
                      }}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      mb: 2,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      flexDirection: 'column',
                      height: 300,
                      border: '2px dashed #b0bec5',
                      borderRadius: 8,
                      background: 'linear-gradient(145deg, #f7f9fb, #eef1f4)',
                      color: '#607d8b',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: '#0288d1',
                        background: 'linear-gradient(145deg, #f0f6ff, #e3efff)',
                        color: '#0288d1',
                      },
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="48"
                      height="48"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ marginBottom: 8 }}
                      viewBox="0 0 24 24"
                    >
                      <path d="M3 16v1a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-1" />
                      <polyline points="16 10 12 6 8 10" />
                      <line x1="12" y1="6" x2="12" y2="20" />
                    </svg>
                    <span style={{ fontSize: 16, fontWeight: 500 }}>No image selected</span>
                    <span style={{ fontSize: 13, color: '#90a4ae' }}>Upload an image to preview</span>
                  </Box>
                )} */}


                <FormLabel sx={{ display: 'block', mb: 1, fontWeight: 'bold' }}>
                  Banner Title
                </FormLabel>
                <Input
                  type="text"
                  placeholder="Enter banner title"
                  value={selectedBanner.title}
                  onChange={(e) =>
                    setSelectedBanner(prev => ({ ...prev!, title: e.target.value }))
                  }
                  fullWidth
                  sx={{ mb: 2 }}
                />

                <FormLabel sx={{ display: 'block', mb: 1, fontWeight: 'bold' }}>
                  Banner Subtitle
                </FormLabel>
                <Input
                  type="text"
                  placeholder="Enter banner subtitle"
                  value={selectedBanner.subtitle}
                  onChange={(e) =>
                    setSelectedBanner(prev => ({ ...prev!, subtitle: e.target.value }))
                  }
                  fullWidth
                  sx={{ mb: 2 }}
                />

                <Button
                  onClick={isCreating ? createBanner : updateBanner}
                  disabled={isLoading}
                  variant="contained"
                  color="secondary"
                  fullWidth
                  sx={{ mt: 2, py: 1.5, borderRadius: 2 }}
                >
                  {isLoading ? 'Processing...' : isCreating ? 'Create Banner' : 'Update Banner'}
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
            action={
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={handleSnackbarClose}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Preview Dialog */}
        <Dialog open={previewDialogOpen} onClose={() => setPreviewDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Banner Preview</DialogTitle>
          <DialogContent>
            <img
              src={`data:image/png;base64,${previewImage}`}
              alt="Full Preview"
              style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain' }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewDialogOpen(false)} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Create Banner Popup */}
        <Dialog open={openCreateDialog} onClose={() => setOpenCreateDialog(false)} fullWidth maxWidth="sm">
          <DialogTitle>Create New Banner</DialogTitle>
          <DialogContent>
            <FormLabel sx={{ display: 'block', mt: 1, fontWeight: 'bold' }}>Banner Title</FormLabel>
            <Input
              fullWidth
              type="text"
              placeholder="Enter banner title"
              value={selectedBanner?.title || ''}
              onChange={(e) =>
                setSelectedBanner(prev => ({ ...prev!, title: e.target.value }))
              }
              sx={{ mb: 2 }}
            />

            <FormLabel sx={{ display: 'block', mt: 1, fontWeight: 'bold' }}>Banner Subtitle</FormLabel>
            <Input
              fullWidth
              type="text"
              placeholder="Enter banner subtitle"
              value={selectedBanner?.subtitle || ''}
              onChange={(e) =>
                setSelectedBanner(prev => ({ ...prev!, subtitle: e.target.value }))
              }
              sx={{ mb: 2 }}
            />
            <FormLabel sx={{ display: 'block', mt: 1, fontWeight: 'bold' }}>Banner Type</FormLabel>
            <Input
              type="text"
              placeholder="Enter banner type"
              value={selectedBanner?.type}
              onChange={(e) =>
                setSelectedBanner(prev => ({ ...prev!, type: e.target.value }))
              }
              fullWidth
              sx={{ mb: 2 }}
              disabled={!isCreating}
            />

            <FormLabel sx={{ display: 'block', mt: 1, fontWeight: 'bold' }}>Select Image</FormLabel>
            <Input
              type="file"
              inputProps={{ accept: '.png,.jpg,.jpeg' }}
              onChange={handleFileChange}
              fullWidth
              sx={{ mb: 2 }}
            />

            {selectedBanner?.base64Preview && (
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <img
                  src={selectedBanner.base64Preview}
                  alt="Preview"
                  style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 8 }}
                />
              </Box>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setOpenCreateDialog(false)} color="error">Cancel</Button>
            <Button
              onClick={async () => {
                await createBanner();
                setOpenCreateDialog(false);
              }}
              color="primary"
              variant="contained"
            >
              Upload Banner
            </Button>
          </DialogActions>
        </Dialog>

        {/* Banner Preview */}

        <Dialog
          open={previewDialogOpen}
          onClose={() => setPreviewDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Banner Preview</DialogTitle>
          <DialogContent>
            <img
              src={`data:image/png;base64,${previewImage}`}
              alt="Full Preview"
              style={{
                width: '100%',
                maxHeight: '70vh',
                objectFit: 'contain'
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewDialogOpen(false)} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>



      </Box>
    </div>
  );
};

export default Banner;
